/**
 * Modal de seleção de Catálogo (Autoral / Signature)
 * Exibe na primeira visita à coleção. Persiste escolha em localStorage por 30 dias.
 * URL: ?filter.p.m.custom.cartas=Autoral | =Signature (preserva sort_by e outros filtros)
 */
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'tanyno_catalog_preference';
const PREFERENCE_DAYS = 30;
const VALID_VALUES = ['Autoral', 'Signature'];

function isValidCatalogValue(value) {
  return value && VALID_VALUES.includes(value);
}

/**
 * Obtém o valor do filtro Catálogo na URL atual
 * @param {URL} url
 * @param {string} filterParam
 * @returns {string|null}
 */
function getCatalogFromUrl(url, filterParam) {
  return url.searchParams.get(filterParam);
}

/**
 * Verifica se o usuário está em uma página de coleção
 * @returns {boolean}
 */
function isCollectionPage() {
  return (
    window.location.pathname.includes('/collections/') ||
    document.querySelector('main[data-template]')?.dataset.template?.includes('collection') === true
  );
}

/**
 * Redireciona para a URL atual apenas adicionando/alterando o filtro de catálogo.
 * Preserva sort_by, outros filtros e path; remove apenas o parâmetro page.
 * @param {string} catalogValue - "Autoral" ou "Signature"
 * @param {string} filterParam
 */
function redirectWithCatalogFilter(catalogValue, filterParam) {
  const url = new URL(window.location.href);
  url.searchParams.set(filterParam, catalogValue);
  url.searchParams.delete('page');
  window.location.href = url.toString();
}

/**
 * Salva preferência com timestamp para expiração em 30 dias
 * @param {string} value - "Autoral" ou "Signature"
 */
function savePreference(value) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ value, setAt: Date.now() })
    );
  } catch (e) {
    localStorage.setItem(STORAGE_KEY, value);
  }
}

/**
 * Lê preferência salva; retorna null se expirada (>30 dias) ou inválida
 * @returns {string|null} "Autoral" | "Signature" | null
 */
function getSavedPreference() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = typeof parsed === 'object' && parsed?.value ? parsed.value : raw;
    if (!isValidCatalogValue(value)) return null;
    const setAt = typeof parsed?.setAt === 'number' ? parsed.setAt : 0;
    const maxAge = PREFERENCE_DAYS * 24 * 60 * 60 * 1000;
    if (setAt && Date.now() - setAt > maxAge) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value;
  } catch (e) {
    const v = localStorage.getItem(STORAGE_KEY);
    return isValidCatalogValue(v) ? v : null;
  }
}

/**
 * Garante que o checkbox do filtro Catálogo (Autoral/Signature) mostre visualmente como marcado
 * quando o filtro está na URL. Valores na URL: Autoral | Signature.
 * @param {string} filterParam - ex: filter.p.m.custom.cartas
 */
function syncFilterCheckedState(filterParam) {
  const url = new URL(window.location.href);
  const catalogValue = getCatalogFromUrl(url, filterParam);
  if (!isValidCatalogValue(catalogValue)) return;

  function checkInput(input) {
    if (!input.checked) input.checked = true;
  }

  const paramNameAttr = filterParam.replace(/\./g, '-');
  const catalogFacet = document.querySelector(
    `[data-filter-param-name="${paramNameAttr}"]`
  );

  if (catalogFacet) {
    const input = catalogFacet.querySelector(
      `input[type="checkbox"][value="${catalogValue}"]`
    );
    if (input) {
      checkInput(input);
      return;
    }
  }

  let list = document.querySelectorAll(
    `input[type="checkbox"][name="${filterParam}"][value="${catalogValue}"]`
  );
  if (list.length > 0) {
    list.forEach(checkInput);
    return;
  }

  list = document.querySelectorAll('input[type="checkbox"][name*="cartas"]');
  Array.from(list).filter((el) => el.value === catalogValue).forEach(checkInput);
}

/**
 * Inicializa o modal de seleção de catálogo
 */
function initCatalogSelectionModal() {
  const modal = document.getElementById('catalog-selection-modal');
  if (!modal) return;

  const filterParam = modal.dataset.filterParam || 'filter.p.m.custom.cartas';
  const debugMode = modal.dataset.debugMode === 'true';
  const url = new URL(window.location.href);
  const catalogInUrl = getCatalogFromUrl(url, filterParam);

  // Modo debug: sempre mostrar popup (ignorar localStorage e redirect)
  if (debugMode) {
    if (typeof modal.showDialog === 'function') {
      modal.showDialog();
    } else {
      const dialog = modal.querySelector('dialog');
      if (dialog) dialog.showModal();
    }
    return;
  }

  // Filtro já na URL (ex.: usuário alterou na barra) → atualizar preferência e não mostrar popup
  if (catalogInUrl && isValidCatalogValue(catalogInUrl)) {
    savePreference(catalogInUrl);
    syncFilterCheckedState(filterParam);
    return;
  }

  const savedPreference = getSavedPreference();

  // Preferência salva e ainda válida (não expirada) mas não está na URL → redirecionar com filtro
  if (savedPreference) {
    redirectWithCatalogFilter(savedPreference, filterParam);
    return;
  }

  // Primeira visita: mostrar modal
  if (typeof modal.showDialog === 'function') {
    modal.showDialog();
  } else {
    const dialog = modal.querySelector('dialog');
    if (dialog) dialog.showModal();
  }
}

/**
 * Configura os event listeners do modal
 */
function setupModalListeners() {
  const modal = document.getElementById('catalog-selection-modal');
  if (!modal) return;

  const filterParam = modal.dataset.filterParam || 'filter.p.m.custom.cartas';

  const handleSelect = (e) => {
    const target = e.target.closest('[data-select-catalog]');
    if (!target) return;

    const catalogValue = target.dataset.selectCatalog;
    if (!isValidCatalogValue(catalogValue)) return;

    savePreference(catalogValue);
    redirectWithCatalogFilter(catalogValue, filterParam);
  };

  modal.addEventListener('click', handleSelect);

  // Fechar ao clicar no fundo (área com blur), não nos cards
  const backdrop = modal.querySelector('.catalog-selection-modal__backdrop');
  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop && typeof modal.closeDialog === 'function') {
        modal.closeDialog();
      }
    });
  }

  // Escuta alterações de filtro via barra de filtros para sincronizar preferência
  document.addEventListener(ThemeEvents.FilterUpdate, (e) => {
    const queryParams = e.detail?.queryParams;
    if (!queryParams) return;

    const catalogValue = queryParams.get(filterParam);
    if (isValidCatalogValue(catalogValue)) {
      savePreference(catalogValue);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  });
}

function runCatalogModal() {
  if (!isCollectionPage()) return;

  const modal = document.getElementById('catalog-selection-modal');
  if (!modal) return;

  const filterParam = modal.dataset.filterParam || 'filter.p.m.custom.cartas';

  setupModalListeners();
  initCatalogSelectionModal();

  function runSync() {
    syncFilterCheckedState(filterParam);
  }

  runSync();
  setTimeout(runSync, 100);
  setTimeout(runSync, 500);
  setTimeout(runSync, 1200);

  // Observar re-renders da seção (morph): quando os filtros forem substituídos, marcar de novo
  const section = document.querySelector('.collection-wrapper')?.closest('.shopify-section');
  if (section) {
    const observer = new MutationObserver(() => {
      setTimeout(runSync, 50);
    });
    observer.observe(section, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runCatalogModal);
} else {
  runCatalogModal();
}
